window.theme = window.theme || {};

class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
        theme.initMiniCartActionsAutoScroll?.();
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // While the drawer is open (upsell adds, gift wrap, quick view),
    // patch the money-touched regions instead of swapping the whole
    // drawer — keeps open collapsibles, scroll and upsell state intact.
    const drawerOpen = this.drawer && !!this.drawer.querySelector('details[open]');

    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) return;

      if (
        section.id === 'mini-cart' &&
        drawerOpen &&
        window.theme && theme.patchMiniCart &&
        parsedState.sections[section.id] &&
        theme.patchMiniCart(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector))
      ) return;

      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    this.open();
    theme.initMiniCartActionsAutoScroll?.();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);

/**
 * Seamless auto-scrolling carousel for the mini cart action bar.
 * When the actions overflow their container, the row is padded with
 * non-interactive copies of itself and scrolled continuously, wrapping
 * back by one full set so the loop never shows an edge.
 */
const MINI_CART_MARQUEE_SPEED = 28; // px per second

const stopMiniCartMarquee = (actions) => {
  if (actions.miniCartMarqueeStop) actions.miniCartMarqueeStop();

  actions.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
  actions.classList.remove('mini-cart__actions--marquee');
  actions.scrollLeft = 0;
};

const startMiniCartMarquee = (actions) => {
  stopMiniCartMarquee(actions);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!actions.isConnected || !actions.clientWidth) return;

  const items = Array.from(actions.children);
  if (!items.length) return;

  const baseWidth = actions.scrollWidth;
  if (baseWidth <= actions.clientWidth + 1) return;

  // Duplicate the whole set until a full base width can be scrolled through,
  // which is what makes the wrap invisible.
  for (let total = baseWidth; total < baseWidth + actions.clientWidth; total += baseWidth) {
    items.forEach((item) => {
      const clone = item.cloneNode(true);

      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('open');

      // Keep the summary only: the popups carry ids and form fields that
      // must not be duplicated in the document.
      if (clone.tagName === 'DETAILS') {
        Array.from(clone.children).forEach((child) => {
          if (child.tagName !== 'SUMMARY') child.remove();
        });
      }

      const summary = clone.querySelector('summary');
      if (summary) summary.setAttribute('tabindex', '-1');

      clone.miniCartSource = item;
      actions.appendChild(clone);
    });
  }

  actions.classList.add('mini-cart__actions--marquee');

  const sign = getComputedStyle(actions).direction === 'rtl' ? -1 : 1;
  let position = 0;
  let frameId = null;
  let paused = false;
  let lastTime = performance.now();

  const tick = (time) => {
    if (!actions.isConnected) {
      stopMiniCartMarquee(actions);
      return;
    }

    const delta = Math.min(time - lastTime, 100);
    lastTime = time;

    if (!paused && !actions.querySelector('details[open]')) {
      position += delta * MINI_CART_MARQUEE_SPEED / 1000;
      if (position >= baseWidth) position -= baseWidth;
      actions.scrollLeft = sign * position;
    }

    frameId = requestAnimationFrame(tick);
  };

  const pause = () => { paused = true; };
  const resume = () => {
    // Pick the loop up wherever a manual scroll left it.
    position = Math.abs(actions.scrollLeft) % baseWidth;
    lastTime = performance.now();
    paused = false;
  };

  // A clone is a decoy — hand the click to the action it was copied from.
  const onClick = (event) => {
    const clone = event.target.closest('[data-marquee-clone]');
    if (!clone) return;

    event.preventDefault();
    event.stopPropagation();

    const summary = clone.miniCartSource && clone.miniCartSource.querySelector('summary');
    if (summary) summary.click();
  };

  actions.addEventListener('click', onClick);
  actions.addEventListener('pointerenter', pause);
  actions.addEventListener('pointerleave', resume);
  actions.addEventListener('touchstart', pause, { passive: true });
  actions.addEventListener('touchend', resume);
  actions.addEventListener('touchcancel', resume);
  actions.addEventListener('focusin', pause);
  actions.addEventListener('focusout', resume);

  actions.miniCartMarqueeStop = () => {
    if (frameId) cancelAnimationFrame(frameId);
    actions.removeEventListener('click', onClick);
    actions.removeEventListener('pointerenter', pause);
    actions.removeEventListener('pointerleave', resume);
    actions.removeEventListener('touchstart', pause);
    actions.removeEventListener('touchend', resume);
    actions.removeEventListener('touchcancel', resume);
    actions.removeEventListener('focusin', pause);
    actions.removeEventListener('focusout', resume);
    actions.miniCartMarqueeStop = null;
  };

  frameId = requestAnimationFrame(tick);
};

theme.initMiniCartActionsAutoScroll = () => {
  document.querySelectorAll('.mini-cart__actions').forEach((actions) => {
    // The drawer can be measured at zero width while it is still closed,
    // so re-run whenever the row actually changes size.
    if (!actions.miniCartResizeObserver && typeof ResizeObserver !== 'undefined') {
      let lastWidth = actions.clientWidth;

      actions.miniCartResizeObserver = new ResizeObserver(() => {
        if (actions.clientWidth === lastWidth) return;
        lastWidth = actions.clientWidth;
        startMiniCartMarquee(actions);
      });

      actions.miniCartResizeObserver.observe(actions);
    }

    // Custom action icons are lazy-loaded, so the row can still grow after
    // the first measurement.
    actions.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => startMiniCartMarquee(actions), { once: true });
    });

    startMiniCartMarquee(actions);
  });
};

document.addEventListener('cartdrawer:opened', () => {
  requestAnimationFrame(() => theme.initMiniCartActionsAutoScroll?.());
});

document.addEventListener('cart:updated', () => {
  requestAnimationFrame(() => theme.initMiniCartActionsAutoScroll?.());
});
